/**
 * TanStack Query read hooks for the agent Tasks list (plan
 * plans/2026-08-08-reminders-and-tasks.md section 5).
 *
 * Wraps:
 *   GET /api/v1/agent/tasks?status=...  -> useTasks
 *   GET /api/v1/agent/tasks/count       -> useTaskCount
 *
 * The count polls every 30s so the sidebar badge stays roughly fresh
 * without a websocket - same cadence as the Inbox badge. Mutations
 * (useTaskMutations) invalidate the shared TASKS_QUERY_KEY prefix,
 * which covers both the lists and the count.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  TaskCountResponse,
  TaskResponse,
  TaskStatus,
} from "@/types/api";

/** Prefix every task-related cache entry hangs under, so one
 * invalidate after a mutation refreshes the lists AND the badge. */
export const TASKS_QUERY_KEY = ["tasks"] as const;
export const TASKS_COUNT_QUERY_KEY = ["tasks", "count"] as const;

export function tasksListQueryKey(status: TaskStatus): readonly unknown[] {
  return [...TASKS_QUERY_KEY, "list", status] as const;
}

/**
 * The calling agent's own list. Open view is server-ordered by
 * ordinal, Done by done_at desc - the component renders rows in the
 * order they arrive and never re-sorts.
 */
export function useTasks(
  status: TaskStatus,
  limit = 100,
): UseQueryResult<TaskResponse[]> {
  return useQuery({
    queryKey: [...tasksListQueryKey(status), limit],
    queryFn: async () => {
      const response = await api.get<TaskResponse[]>("/agent/tasks", {
        params: { status, limit },
      });
      return response.data;
    },
  });
}

/** Open-task count for the sidebar badge. 30s poll, same as Inbox. */
export function useTaskCount(): UseQueryResult<TaskCountResponse> {
  return useQuery({
    queryKey: TASKS_COUNT_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<TaskCountResponse>("/agent/tasks/count");
      return response.data;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}
